// src/pages/ConsultantAssessment.js
// Consultant post-meeting assessment — checkbox observed/not-observed per question
// Stored with meetingId + questionId linkage for Team Perspective vs Consultant Observed comparison

import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  collection, addDoc, query, where, getDocs, doc, getDoc,
  updateDoc, serverTimestamp
} from 'firebase/firestore/lite';
import { db } from '../firebase/config';
import { useAuth } from '../context/AuthContext';
import { DEFAULT_CATEGORIES, withCurrentLabels } from '../utils/surveyQuestions';
import PageLoading from '../components/PageLoading';

export default function ConsultantAssessment() {
  const { meetingId } = useParams();
  const navigate = useNavigate();
  const { currentUser, userProfile } = useAuth();

  const [meeting, setMeeting] = useState(null);
  const [team, setTeam] = useState(null);
  const [categories, setCategories] = useState(DEFAULT_CATEGORIES);
  const [observed, setObserved] = useState({}); // { [questionId]: true/false }
  const [currentCat, setCurrentCat] = useState(0);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [existingResponseId, setExistingResponseId] = useState(null);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    loadData();
  }, [meetingId]);

  async function loadData() {
    try {
      // Load meeting
      if (meetingId) {
        const meetingSnap = await getDoc(doc(db, 'meetings', meetingId));
        if (meetingSnap.exists()) {
          const meetingData = { id: meetingSnap.id, ...meetingSnap.data() };
          setMeeting(meetingData);

          // Load team
          if (meetingData.teamId) {
            const teamSnap = await getDoc(doc(db, 'teams', meetingData.teamId));
            if (teamSnap.exists()) {
              const teamData = { id: teamSnap.id, ...teamSnap.data() };
              setTeam(teamData);
              if (teamData.customQuestions) setCategories(withCurrentLabels(teamData.customQuestions));
            }
          }
        }
      }

      // Check for existing consultant observation for this meeting
      const existing = await getDocs(
        query(
          collection(db, 'consultantObservations'),
          where('meetingId', '==', meetingId),
          where('consultantId', '==', currentUser.uid)
        )
      );
      if (!existing.empty) {
        const existingDoc = existing.docs[0];
        setExistingResponseId(existingDoc.id);
        setObserved(existingDoc.data().observed || {});
        setSubmitted(true);
      }
    } catch (err) {
      console.error('Error loading consultant assessment:', err);
    } finally {
      setLoading(false);
    }
  }

  function toggleObserved(questionId) {
    setObserved(prev => ({
      ...prev,
      [questionId]: !prev[questionId],
    }));
    // Reset submitted state if editing
    if (submitted) setSubmitted(false);
  }

  async function handleSubmit() {
    setSubmitting(true);
    try {
      const payload = {
        meetingId,
        teamId: meeting?.teamId,
        consultantId: currentUser.uid,
        consultantEmail: currentUser.email,
        observed, // { [questionId]: true/false }
        submittedAt: serverTimestamp(),
        // Store question metadata for easier querying
        questionIds: Object.keys(observed),
        observedCount: Object.values(observed).filter(Boolean).length,
        totalQuestions: categories.reduce((sum, cat) => sum + cat.questions.length, 0),
      };

      if (existingResponseId) {
        await updateDoc(doc(db, 'consultantObservations', existingResponseId), payload);
      } else {
        const ref = await addDoc(collection(db, 'consultantObservations'), payload);
        setExistingResponseId(ref.id);
      }

      setSubmitted(true);
    } catch (err) {
      console.error('Error submitting observations:', err);
      alert('Failed to save. Please try again.');
    } finally {
      setSubmitting(false);
    }
  }

  if (loading) return <PageLoading text="Loading assessment..." />;

  const cat = categories[currentCat];
  const isLastCat = currentCat === categories.length - 1;
  const totalQuestions = categories.reduce((sum, c) => sum + c.questions.length, 0);
  const observedCount = Object.values(observed).filter(Boolean).length;

  // Progress: how many categories have at least one answer
  const answeredCats = categories.filter(c =>
    c.questions.some(q => observed[q.id] !== undefined)
  ).length;

  if (submitted) {
    return (
      <div className="fade-up">
        <div className="page-header">
          <h2>Observations Saved</h2>
          <p>
            You observed <strong>{observedCount}</strong> of {totalQuestions} behaviors
            {meeting ? ` in "${meeting.name}"` : ''}.
          </p>
        </div>

        <div className="card" style={{ marginBottom: 24 }}>
          <div className="card-title">Summary by Category</div>
          <div style={{ marginTop: 16, display: 'flex', flexDirection: 'column', gap: 12 }}>
            {categories.map(c => {
              const catObserved = c.questions.filter(q => observed[q.id]).length;
              const pct = Math.round((catObserved / c.questions.length) * 100);
              return (
                <div key={c.id}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                    <span style={{ fontSize: '0.85rem', fontWeight: 600, color: 'var(--ink)' }}>{c.label}</span>
                    <span style={{ fontSize: '0.8rem', color: 'var(--ink-muted)' }}>
                      {catObserved}/{c.questions.length} observed
                    </span>
                  </div>
                  <div style={{ height: 6, background: 'var(--border)', borderRadius: 3 }}>
                    <div style={{
                      height: '100%',
                      width: `${pct}%`,
                      background: c.color,
                      borderRadius: 3,
                      transition: 'width 0.4s ease',
                    }} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div style={{ display: 'flex', gap: 12 }}>
          <button className="btn btn-secondary" onClick={() => setSubmitted(false)}>
            ← Edit observations
          </button>
          <button
            className="btn btn-primary"
            onClick={() => navigate(meetingId ? `/meetings/${meetingId}/report` : '/meetings')}
          >
            View Meeting Report →
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="fade-up">
      <div className="page-header">
        <h2>Consultant Observations</h2>
        <p>
          {meeting ? `Meeting: ${meeting.name}` : 'Post-meeting observation'}
          {team ? ` · ${team.name}` : ''}
        </p>
      </div>

      {/* Progress */}
      <div style={{ marginBottom: 28 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
          <span style={{ fontSize: '0.8rem', color: 'var(--ink-muted)', fontWeight: 600 }}>
            Category {currentCat + 1} of {categories.length}
          </span>
          <span style={{ fontSize: '0.8rem', color: 'var(--ink-muted)' }}>
            {observedCount} behaviors marked as observed
          </span>
        </div>
        <div style={{ height: 4, background: 'var(--border)', borderRadius: 2 }}>
          <div style={{
            height: '100%',
            width: `${((currentCat + 1) / categories.length) * 100}%`,
            background: 'var(--accent)',
            borderRadius: 2,
            transition: 'width 0.3s ease',
          }} />
        </div>

        {/* Category tabs */}
        <div style={{ display: 'flex', gap: 8, marginTop: 12, flexWrap: 'wrap' }}>
          {categories.map((c, idx) => (
            <button
              key={c.id}
              onClick={() => setCurrentCat(idx)}
              style={{
                padding: '4px 12px',
                borderRadius: 20,
                border: `2px solid ${idx === currentCat ? c.color : 'var(--border)'}`,
                background: idx === currentCat ? c.color : 'transparent',
                color: idx === currentCat ? 'white' : 'var(--ink-muted)',
                fontSize: '0.75rem',
                fontWeight: 600,
                cursor: 'pointer',
                transition: 'all 0.2s',
              }}
            >
              {c.label}
            </button>
          ))}
        </div>
      </div>

      {/* Current category */}
      <div className="card" style={{ marginBottom: 24 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
          <div style={{ width: 12, height: 12, borderRadius: '50%', background: cat.color }} />
          <div className="card-title" style={{ margin: 0 }}>{cat.label}</div>
        </div>
        <div className="card-subtitle" style={{ marginBottom: 20 }}>{cat.description}</div>
        <div style={{ fontSize: '0.78rem', color: 'var(--ink-muted)', marginBottom: 20, fontStyle: 'italic' }}>
          Check each behavior you observed during this meeting. Leave unchecked if not observed.
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
          {cat.questions.map((q, qi) => {
            const isObserved = !!observed[q.id];
            return (
              <div
                key={q.id}
                onClick={() => toggleObserved(q.id)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 14,
                  padding: '14px 12px',
                  borderRadius: 8,
                  cursor: 'pointer',
                  transition: 'background 0.15s',
                  background: isObserved ? `${cat.color}10` : 'transparent',
                  borderBottom: qi < cat.questions.length - 1 ? '1px solid var(--border)' : 'none',
                }}
                onMouseEnter={e => {
                  if (!isObserved) e.currentTarget.style.background = 'var(--bg-soft)';
                }}
                onMouseLeave={e => {
                  e.currentTarget.style.background = isObserved ? `${cat.color}10` : 'transparent';
                }}
              >
                {/* Checkbox */}
                <div style={{
                  width: 22,
                  height: 22,
                  borderRadius: 5,
                  border: `2px solid ${isObserved ? cat.color : 'var(--border)'}`,
                  background: isObserved ? cat.color : 'transparent',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                  transition: 'all 0.15s',
                }}>
                  {isObserved && (
                    <span style={{ color: 'white', fontSize: '0.75rem', fontWeight: 700, lineHeight: 1 }}>✓</span>
                  )}
                </div>

                {/* Question text */}
                <div style={{ flex: 1 }}>
                  <span style={{
                    fontSize: '0.88rem',
                    color: isObserved ? 'var(--ink)' : 'var(--ink-soft)',
                    fontWeight: isObserved ? 500 : 400,
                    lineHeight: 1.5,
                  }}>
                    <span style={{ color: 'var(--ink-muted)', fontSize: '0.72rem', marginRight: 8 }}>
                      {qi + 1}.
                    </span>
                    {q.text}
                  </span>
                </div>

                {/* Observed label */}
                {isObserved && (
                  <span style={{
                    fontSize: '0.72rem',
                    fontWeight: 700,
                    color: cat.color,
                    textTransform: 'uppercase',
                    letterSpacing: '0.06em',
                    flexShrink: 0,
                  }}>
                    Observed
                  </span>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Navigation */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <button
          className="btn btn-secondary"
          onClick={() => setCurrentCat(Math.max(0, currentCat - 1))}
          disabled={currentCat === 0}
        >
          ← Previous
        </button>

        {isLastCat ? (
          <button
            className="btn btn-primary"
            onClick={handleSubmit}
            disabled={submitting}
          >
            {submitting
              ? <><span className="spinner" /> Saving...</>
              : existingResponseId
              ? 'Update observations →'
              : 'Save observations →'}
          </button>
        ) : (
          <button
            className="btn btn-primary"
            onClick={() => setCurrentCat(Math.min(categories.length - 1, currentCat + 1))}
          >
            Next: {categories[currentCat + 1]?.label} →
          </button>
        )}
      </div>
    </div>
  );
}
